import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { LogIn, RefreshCw, Key, LogOut } from 'lucide-react';
import type { TranslateFn } from '../../types';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_spa-bookings-5/artifacts/syk2guux_Angel-Massage-logo-png.png";

interface InlineLanguageSwitcherProps {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

const InlineLanguageSwitcher = ({ variant = "ghost" }: InlineLanguageSwitcherProps) => {
  const { language, setLanguage } = useLanguage();
  return (
    <Button variant={variant} size="sm" onClick={() => setLanguage(language === 'en' ? 'th' : 'en')}>
      <span className="mr-1">&#127760;</span>
      {language === 'en' ? 'ไทย' : 'EN'}
    </Button>
  );
};

interface AdminHeaderProps {
  user: { name?: string } | null;
  t: TranslateFn;
  fetchData: () => void;
  onPasswordChange: () => void;
  onLogout: () => void;
}

export const AdminHeader = ({ user, t, fetchData, onPasswordChange, onLogout }: AdminHeaderProps) => {
  return (
    <header className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <img src={LOGO_URL} alt="Angel Massage" className="h-10 sm:h-12 w-auto shrink-0" />
            <div className="min-w-0">
              <h1 className="text-sm sm:text-lg font-semibold text-gray-800 truncate">{t('admin.title')}</h1>
              <p className="text-xs text-gray-500 hidden sm:block">{t('admin.welcome')}, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <InlineLanguageSwitcher variant="outline" />
            <Button variant="outline" size="sm" onClick={fetchData} className="px-2 sm:px-3" title={t('common.refresh')}>
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">{t('common.refresh')}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={onPasswordChange} className="px-2 sm:px-3 hidden sm:flex" title={t('admin.changePassword')} data-testid="change-password-btn">
              <Key className="w-4 h-4" />
              <span className="hidden md:inline ml-2">{t('admin.changePassword')}</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout} className="px-2 sm:px-3" title={t('common.logout')}>
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">{t('common.logout')}</span>
            </Button>
          </div>
        </div>
        {/* Mobile-only secondary row */}
        <div className="flex sm:hidden items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500">{t('admin.welcome')}, {user?.name}</p>
          <Button variant="outline" size="sm" onClick={onPasswordChange} className="text-xs px-2 h-7" data-testid="change-password-btn-mobile">
            <Key className="w-3 h-3 mr-1" /> {t('admin.changePassword')}
          </Button>
        </div>
      </div>
    </header>
  );
};
